// src/pages/school_exams/template_builder/Toolbar.js
import React, { useRef } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Divider,
  Typography,
  Slider,
  Tooltip,
} from "@mui/material";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TableChartIcon from "@mui/icons-material/TableChart";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SaveIcon from "@mui/icons-material/Save";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import GridOnIcon from "@mui/icons-material/GridOn";
import GridOffIcon from "@mui/icons-material/GridOff";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LayersIcon from "@mui/icons-material/Layers";

const Toolbar = ({
  addElement,
  addQuickBlock,
  onSave,
  onLoad,
  deleteSelected,
  moveLayer,
  duplicateSelected,
  toggleLockSelected,
  alignSelected,
  onBackgroundUpload,
  templateName,
  setTemplateName,
  zoom,
  setZoom,
  showGrid,
  setShowGrid,
  hasSelection,
  hasMultipleSelection,
  anyLocked,
}) => {
  const fileInputRef = useRef(null);

  const handleBackgroundChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onBackgroundUpload(file);
  };

  const handleZoomChange = (_, value) => {
    setZoom(value);
  };

  return (
    <Box
      sx={{
        width: 260,
        borderRight: "1px solid #e5e7eb",
        bgcolor: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        p: 2,
        gap: 1.5,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Tools
      </Typography>

      {/* Template name */}
      <TextField
        size="small"
        label="Template Name"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
        fullWidth
      />

      {/* Background upload */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<ImageIcon />}
        sx={{ mt: 0.5 }}
        onClick={() => fileInputRef.current?.click()}
      >
        Background Image/PDF
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        hidden
        onChange={handleBackgroundChange}
      />

      <Divider sx={{ my: 1 }} />

      {/* Add elements */}
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
        Add Elements
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("text")}
          startIcon={<TextFieldsIcon />}
        >
          Text
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("photo")}
          startIcon={<ImageIcon />}
        >
          Photo
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("line")}
          startIcon={<HorizontalRuleIcon />}
        >
          Line
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("table")}
          startIcon={<TableChartIcon />}
        >
          Table
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("rect")}
          startIcon={<CropSquareIcon />}
        >
          Box
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => addElement("circle")}
          startIcon={<RadioButtonUncheckedIcon />}
        >
          Circle
        </Button>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Selection actions */}
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
        Selection
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
        <Tooltip title="Duplicate">
          <span>
            <IconButton
              size="small"
              disabled={!hasSelection}
              onClick={duplicateSelected}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={anyLocked ? "Unlock" : "Lock"}>
          <span>
            <IconButton
              size="small"
              disabled={!hasSelection}
              onClick={toggleLockSelected}
            >
              {anyLocked ? (
                <LockOpenIcon fontSize="small" />
              ) : (
                <LockIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Delete selected">
          <span>
            <IconButton
              size="small"
              disabled={!hasSelection}
              onClick={deleteSelected}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Align */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#6b7280" }}
        >
          Align
        </Typography>
        <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
          Section available when multiple items are selected.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {[
            ["LEFT", "left"],
            ["RIGHT", "right"],
            ["TOP", "top"],
            ["BOTTOM", "bottom"],
            ["CENTER X", "center_h"],
            ["CENTER Y", "center_v"],
          ].map(([label, mode]) => (
            <Button
              key={mode}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, px: 0.8 }}
              disabled={!hasSelection}
              onClick={() => alignSelected(mode)}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Layer controls */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#6b7280" }}
        >
          Layers
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
          <Tooltip title="Bring forward">
            <span>
              <IconButton
                size="small"
                disabled={!hasSelection}
                onClick={() => moveLayer("up")}
              >
                <LayersIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Send backward">
            <span>
              <IconButton
                size="small"
                disabled={!hasSelection}
                onClick={() => moveLayer("down")}
              >
                <LayersIcon
                  fontSize="small"
                  sx={{ transform: "rotate(180deg)" }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Bring to front">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!hasSelection}
                onClick={() => moveLayer("top")}
              >
                Top
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Send to back">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!hasSelection}
                onClick={() => moveLayer("bottom")}
              >
                Bottom
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* View: Zoom + Grid */}
      <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
        View
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Slider
          size="small"
          min={0.5}
          max={2}
          step={0.1}
          value={zoom}
          onChange={handleZoomChange}
          sx={{ mx: 1 }}
        />
        <IconButton
          size="small"
          onClick={() => setZoom(Math.min(2, zoom + 0.1))}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>
      <Typography variant="caption" sx={{ color: "#6b7280" }}>
        Zoom: {(zoom * 100).toFixed(0)}%
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={showGrid ? <GridOffIcon /> : <GridOnIcon />}
          onClick={() => setShowGrid((prev) => !prev)}
        >
          {showGrid ? "Hide grid" : "Show grid"}
        </Button>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Save / Load */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          fullWidth
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSave}
        >
          Save
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<FolderOpenIcon />}
          onClick={onLoad}
        >
          Load
        </Button>
      </Box>

      {/* Quick Blocks */}
      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" sx={{ fontWeight: 600, color: "#6b7280" }}>
        Quick Blocks
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => addQuickBlock("school_header")}
        >
          School Header
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => addQuickBlock("exam_info")}
        >
          Exam Title Block
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => addQuickBlock("student_info")}
        >
          Student Info Block
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => addQuickBlock("result_summary")}
        >
          Result Summary Block
        </Button>

        <Button
          size="small"
          variant="outlined"
          onClick={() => addQuickBlock("photo")}
        >
          Auto Photo Block
        </Button>
      </Box>
    </Box>
  );
};

export default Toolbar;
